import Image from 'next/image'
import clsx from 'clsx'
import xss from 'xss'

import { Chips } from '@wartek-id/chips'
import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button-v1'
import { Sheet, SheetContent, SheetOverlay } from '@wartek-id/sheet-v1'

import { useAuth } from 'utils/auth'
import DocumentListItem from './DocumentListItem'
import styles from './Detail.module.css'

import type { IInfoTerkiniDetail } from 'types/info-terkini'

interface InfoTerkiniDetailProps {
  data: IInfoTerkiniDetail
  showSurveySheet: boolean
  onCloseSurveySheet: () => void
  onExitButtonPressed: () => void
}

const Detail = ({
  data,
  showSurveySheet,
  onCloseSurveySheet,
  onExitButtonPressed,
}: InfoTerkiniDetailProps) => {
  const { session } = useAuth()

  const onSurveyClick = () => {
    window.open(data.surveyLink, '_blank')
  }

  const isLogin = !!session

  return (
    <div className="p-4">
      {data.thumbnailUrl && (
        <img
          className="mb-2 w-full"
          src={data.thumbnailUrl}
          alt={data.title}
          data-testid="thumbnail"
        />
      )}
      <Text variant="heading-lg">{data.title}</Text>

      <div className="mt-2">
        <Chips size="sm">
          <Icon color="inherit" fontSize="small">
            info_outlined
          </Icon>
          <span>{data.category.name}</span>
        </Chips>
      </div>

      {data.effectiveDate && (
        <div
          className="bg-gray-10 rounded flex p-3 mt-2 items-center"
          data-testid="effectiveDate"
        >
          <Icon>info</Icon>

          <Text variant="body-sm" className="ml-2">
            Berlaku mulai <span>{data.effectiveDate}</span>
          </Text>
        </div>
      )}

      {data.name && (
        <Text
          color="subdued"
          variant="body-sm"
          className="mt-2"
          data-testid="name"
        >
          {data.name}
        </Text>
      )}

      <div
        className={clsx('mt-5', styles.summary)}
        data-testid="summary"
        dangerouslySetInnerHTML={{ __html: xss(data.summary) }}
      ></div>

      {data.docs.length > 0 && (
        <div className="mt-8" data-testid="document-list">
          <Text variant="heading-md">Dokumen Pendukung</Text>
          {data.docs.map((document) => (
            <DocumentListItem key={document.id} document={document} />
          ))}
        </div>
      )}

      {data.surveyLink && isLogin && (
        <>
          <div
            className="mt-8 bg-gray-10 rounded p-4"
            data-testid="survey-banner"
          >
            <Image
              src="/icon/survey.svg"
              width={40}
              height={40}
              alt="Ikon survei"
            />
            <Text variant="heading-sm" className="mt-2">
              Bantu Kemendikbudristek sampaikan kebijakan dengan lebih baik!
            </Text>

            <Text variant="body" className="mt-2">
              Mohon jawab pertanyaan singkat terkait pemahaman Anda tentang
              kebijakan ini.
            </Text>

            <Button
              color="white"
              fullWidth
              className="mt-2"
              onClick={onSurveyClick}
            >
              Jawab Pertanyaan
            </Button>
          </div>

          <Sheet
            closeOnOverlayClick
            onClose={onCloseSurveySheet}
            position="bottom"
            sheetSize="auto"
            type="alert"
            isOpen={showSurveySheet}
          >
            <SheetOverlay />
            <SheetContent className="max-w-xl m-auto">
              <div
                className="px-4 py-6"
                data-testid="confirm-exit-before-survey"
              >
                <Image
                  src="/icon/survey.svg"
                  width={40}
                  height={40}
                  alt="Ikon survei"
                />
                <Text variant="heading-sm" className="mt-4">
                  Bantu Kemendikbudristek sampaikan kebijakan dengan lebih baik!
                </Text>

                <Text className="mt-4">
                  Mohon jawab pertanyaan singkat terkait{' '}
                  <strong>kebijakan yang baru Anda baca.</strong> Jawaban Anda
                  akan membantu tim Kemendikbudristek mengetahui bagaimana
                  kebijakan ini dipahami.
                </Text>

                <Button
                  color="white"
                  fullWidth
                  className="mt-4"
                  onClick={onExitButtonPressed}
                >
                  Keluar
                </Button>
                <Button
                  color="black"
                  fullWidth
                  className="mt-2"
                  onClick={onSurveyClick}
                >
                  Jawab Pertanyaan
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </>
      )}
    </div>
  )
}

export default Detail
